import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Observable } from "rxjs";
import { catchError, map, switchMap, tap } from "rxjs/operators";
import { of } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import { ToastrService } from "../../shared/toastr.service";
import {
  GET_DISCLAIMERS,
  GetDisclaimersSuccessAction,
  GetDisclaimersFailureAction,
  CHANGE_DISCLAIMER,
  ChangeDisclaimerAction,
  ChangeDisclaimerSuccessAction,
  ChangeDisclaimerFailureAction,
  REMOVE_DISCLAIMER,
  RemoveDisclaimerAction,
  RemoveDisclaimerSuccessAction,
  RemoveDisclaimerFailureAction,
  ADD_DISCLAIMER,
  ADD_DISCLAIMER_FAILURE,
  AddDisclaimerAction,
  AddDisclaimerSuccessAction,
  AddDisclaimerFailureAction,
} from "./disclaimers.actions";
import { DisclaimersService } from "./disclaimers.service";
import {
  AddDisclaimerObject,
  Disclaimers,
  Disclaimer,
} from "./disclaimers.interface";
import { MatDialog } from "@angular/material/dialog";
import { LimitPlanDialogComponent } from "../../shared/limit-plan-dialog/limit-plan-dialog.component";

@Injectable()
export class DisclaimersEffects {
  getDisclaimers$: Observable<unknown> = createEffect(() => {
    return this.actions$.pipe(
      ofType(GET_DISCLAIMERS),
      switchMap(() => {
        return this.disclaimersService.getDisclaimers().pipe(
          switchMap((response: Disclaimers) => {
            return [new GetDisclaimersSuccessAction(response)];
          }),
          catchError((error) => of(new GetDisclaimersFailureAction(error))),
        );
      }),
    );
  });

  changeDisclaimer$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(CHANGE_DISCLAIMER),
      map((action: ChangeDisclaimerAction) => action.payload),
      switchMap((payload: Disclaimer) => {
        return this.disclaimersService.changeDisclaimer(payload).pipe(
          switchMap(() => {
            this.toastr.success(
              this.translate.instant(
                "addons.disclaimers.editSuccess",
              ) as string,
            );
            return [new ChangeDisclaimerSuccessAction(payload)];
          }),
          catchError((error) => of(new ChangeDisclaimerFailureAction(error))),
        );
      }),
    ),
  );

  removeDisclaimer$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(REMOVE_DISCLAIMER),
      map((action: RemoveDisclaimerAction) => action.payload),
      switchMap((payload: string) => {
        return this.disclaimersService.removeDisclaimer(payload).pipe(
          switchMap(() => {
            this.toastr.success(
              this.translate.instant(
                "addons.disclaimers.deleteSuccess",
              ) as string,
            );
            return [new RemoveDisclaimerSuccessAction(payload)];
          }),
          catchError((error) => of(new RemoveDisclaimerFailureAction(error))),
        );
      }),
    ),
  );

  addDisclaimer$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(ADD_DISCLAIMER),
      map((action: AddDisclaimerAction) => action.payload),
      switchMap((payload: AddDisclaimerObject) => {
        return this.disclaimersService.addDisclaimer(payload).pipe(
          switchMap((response) => {
            this.toastr.success(
              this.translate.instant("addons.disclaimers.addSuccess") as string,
            );
            return [new AddDisclaimerSuccessAction(response as Disclaimer)];
          }),
          catchError((error) => of(new AddDisclaimerFailureAction(error))),
        );
      }),
    ),
  );

  addDisclaimerFailure$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ADD_DISCLAIMER_FAILURE),
        tap((action: AddDisclaimerFailureAction) => {
          const payload = action.payload as
            | { error?: { message?: string }; message?: string }
            | { error?: string }
            | string
            | undefined;
          const errorMessage =
            (typeof payload === "object" &&
              payload !== null &&
              ("error" in payload
                ? typeof payload.error === "object" && payload.error !== null
                  ? "message" in payload.error
                    ? payload.error.message
                    : undefined
                  : typeof payload.error === "string"
                    ? payload.error
                    : undefined
                : "message" in payload
                  ? payload.message
                  : undefined)) ||
            (typeof payload === "string" ? payload : "") ||
            "";
          if (
            typeof errorMessage === "string" &&
            errorMessage.includes("DISCLAIMER_LIMIT_REACHED")
          ) {
            this.dialog.open(LimitPlanDialogComponent, {
              width: "500px",
              maxWidth: "90vw",
              data: { plan: "basic", type: "disclaimer" },
            });
          } else {
            this.toastr.error(
              this.translate.instant("addons.disclaimers.addError") as string,
            );
          }
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private disclaimersService: DisclaimersService,
    private translate: TranslateService,
    private toastr: ToastrService,
    private dialog: MatDialog,
  ) {}
}
